import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { History, Compass, Trash2, Download, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/api";

export default function ClientKundliHistory({ clientId, refreshTrigger }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/kundli-history`);
      setItems(data);
    } catch (err) { console.error("kundli history load failed:", err?.message); }
  }, [clientId]);
  useEffect(() => { load(); }, [load, refreshTrigger]);

  const remove = async (id) => {
    if (!window.confirm("Delete this Kundli snapshot?")) return;
    await api.delete(`/kundli-history/${id}`);
    setItems(items.filter(i => i.id !== id));
    toast.success("Deleted");
  };

  const downloadPdf = async (rec) => {
    setBusy(rec.id);
    try {
      const t = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/kundli/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ kundli: rec.kundli, analysis: rec.analysis, client_name: rec.client_name, record_id: rec.id }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(rec.client_name || "Kundli").replace(/\s+/g, "_")}_Kundli.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF failed"); }
    setBusy(null);
  };

  if (items.length === 0) return null;

  return (
    <Card className="p-5 rounded-2xl border-slate-200 dark:border-slate-800" data-testid="kundli-history-panel">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-amber-500" />
        <h3 className="font-display text-lg">Past Kundli Readings</h3>
        <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
      </div>
      <div className="space-y-2">
        {items.map(rec => (
          <div key={rec.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 dark:border-slate-800 hover:bg-amber-50/30 dark:hover:bg-amber-500/5" data-testid={`kundli-history-item-${rec.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Compass className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span className="font-mono text-xs text-slate-500">{(rec.created_at || "").slice(0, 16).replace("T", " ")}</span>
                {rec.serial_no && <Badge variant="secondary" className="font-mono text-[10px]">{rec.serial_no}</Badge>}
              </div>
              <div className="text-sm mt-0.5">
                Lagna: <span className="font-medium">{rec.kundli?.ascendant?.rashi}</span> ·
                Moon: <span className="font-medium">{rec.kundli?.moon_sign?.rashi}</span> ·
                Dasha: <span className="font-medium">{rec.kundli?.dasha?.current?.planet}</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">by {rec.created_by_name || "—"}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => downloadPdf(rec)} disabled={busy === rec.id} data-testid={`kundli-history-pdf-${rec.id}`}>
                {busy === rec.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(rec.id)} className="text-red-600" data-testid={`kundli-history-delete-${rec.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
